import { makeStyles } from '@material-ui/core/styles';

// Compact heat-table for per-brand demographic comparison (Age / Gender / Locations).
// Rows = categories, columns = brands (short codes), cells = value on a green heat
// scale with high-contrast bold numbers. Replaces the cramped grouped bar charts.

// Short column code for a brand; full name shows in the legend + on hover.
export const brandCode = (name) => {
  const words = String(name || '').split(/[\s._-]+/).filter(Boolean);
  if (words.length > 1) return words.slice(0, 4).map((w) => w[0]).join('').toUpperCase();
  return String(name || '').replace(/[^a-z0-9]/gi, '').slice(0, 3).toUpperCase();
};

// Green scale: light (#eef6e4) -> dark (#3f7523). Normalised PER ROW (min..max
// across brands for that category) so small, clustered values (e.g. locations all
// at 4-6%) still show a clear gradient instead of washing out. A uniform row gets a
// neutral mid tone. Text flips to white on darker cells so the number stays legible.
function heatStyle(value, min, max) {
  const t = max > min ? (value - min) / (max - min) : 0.5;
  const lerp = (a, b) => Math.round(a + (b - a) * t);
  return {
    background: `rgb(${lerp(238, 63)}, ${lerp(246, 117)}, ${lerp(228, 35)})`,
    color: t > 0.6 ? '#fff' : '#1f2937',
  };
}

export function BrandLegend({ brands = [] }) {
  const classes = useStyles();
  if (!brands.length) return null;
  return (
    <div className={classes.legend}>
      {brands.map((b) => (
        <span key={b.name} className={classes.legendItem}>
          <i className={classes.swatch} style={{ background: b.color }} />
          <b className={classes.code}>{brandCode(b.name)}</b>
          <span className={classes.brandName}>{b.name}</span>
        </span>
      ))}
    </div>
  );
}

export default function DemographicHeatTable({ categories = [], series = [], percent = true }) {
  const classes = useStyles();
  const fmt = (v) => {
    const n = Number(v) || 0;
    if (percent) return `${Math.round(n)}%`;
    return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
  };

  return (
    <div className={classes.wrap}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th className={classes.catHead} />
            {series.map((s) => (
              <th key={s.name} className={classes.brandHead} title={s.name}>
                {brandCode(s.name)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {categories.map((cat, i) => {
            const rowVals = series.map((s) => Number((s.data || [])[i]) || 0);
            const rMin = Math.min(...rowVals);
            const rMax = Math.max(...rowVals);
            return (
              <tr key={cat}>
                <td className={classes.catCell} title={cat}>{cat}</td>
                {series.map((s, j) => (
                  <td
                    key={s.name}
                    className={classes.cell}
                    style={heatStyle(rowVals[j], rMin, rMax)}
                    title={`${s.name} · ${cat}: ${percent ? `${rowVals[j]}%` : rowVals[j]}`}
                  >
                    {fmt(rowVals[j])}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const useStyles = makeStyles(() => ({
  wrap: { overflowX: 'auto', paddingTop: 4 },
  table: { borderCollapse: 'separate', borderSpacing: 3, width: '100%', fontSize: 12, tableLayout: 'fixed' },
  catHead: { width: 62 },
  brandHead: { padding: '2px', fontSize: 10.5, fontWeight: 800, color: '#6b7280', textAlign: 'center' },
  catCell: {
    textAlign: 'left', fontWeight: 800, color: '#111827', fontSize: 11.5,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: 8, width: 62,
  },
  cell: { padding: '7px 2px', textAlign: 'center', fontWeight: 800, borderRadius: 6, fontSize: 12.5 },
  // legend
  legend: { display: 'flex', flexWrap: 'wrap', gap: '6px 16px', margin: '16px 0 4px' },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 },
  swatch: { width: 11, height: 11, borderRadius: 3, flexShrink: 0 },
  code: { fontWeight: 800, color: '#111827', minWidth: 26 },
  brandName: { color: '#6b7280', fontWeight: 600 },
}));
